import { useState } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import LoginForm from 'components/Login/LoginForm';
import ForgotPasswordForm from 'components/ForgotPassword/ForgotPasswordForm';
import RecoverPassword, { RecoverPasswordQueryParams } from 'components/RecoverPassword/RecoverPassword';
import Register from 'components/common/User/Register';
import { Alert, AlertDescription, Button, Heading, Modal, Spinner, Tabs, Text } from 'components/UI';
import { useSignIn, useToast } from 'hooks';
import { specialFont } from 'constants/theme';
import { useSession } from 'contexts';
import { anonymousUser } from 'constants/users';
import { ISettings } from 'types';

type AuthOverlayProps = {
  queryParams?: RecoverPasswordQueryParams;
  theme: ISettings | null;
};

const AuthOverlay = ({ queryParams, theme }: AuthOverlayProps) => {
  const [forgotPassword, setForgotPassword] = useState<boolean>(false);
  const [recoverPassword, setRecoverPassword] = useState<boolean>(!!queryParams?.token);
  const [tab, setTab] = useState<number>(0);
  const [modalOpen, setModalOpen] = useState(true);

  const [showToast] = useToast();
  const [signIn, { loading, error }] = useSignIn();
  const [, setSessionError] = useSession();

  const url = queryParams?.referrerUrl ? decodeURIComponent(queryParams?.referrerUrl) : document.location.href;

  const isAnonymous = theme?.anonymousAccess === 'true';

  const renderError = error ? (
    <Alert variant="subtle" bgColor="transparent" justifyContent="center">
      <AlertDescription>{error}</AlertDescription>
    </Alert>
  ) : null;

  const tabOptions = [
    {
      label: 'LOGIN',
      panel: <LoginForm inputProps={{ textAlign: 'center' }} buttonProps={{ px: 4 }} overlay />,
    },
    {
      label: 'REGISTER',
      panel: <Register url={url} />,
    },
  ];

  const handleAnonymousLogin = async () => {
    await signIn(anonymousUser.email, '');
    setSessionError(undefined);
  };

  const handleGetTabPadding = () => {
    if (error) return 0;
    if (isAnonymous) return 4;

    return 8;
  };

  const renderLostPassword =
    tab === 0 ? (
      <Box textAlign="center" w="full">
        <Button
          mb={isAnonymous ? 7 : 8}
          variant="link"
          onClick={() => {
            setRecoverPassword(false);
            setForgotPassword(true);
          }}
        >
          Lost your password?
        </Button>
      </Box>
    ) : null;

  const renderSkipLink =
    tab === 0 && isAnonymous ? (
      <Box py={4} bgColor="base2Colour.500" textAlign="center">
        <Button
          variant="link"
          fontWeight="medium"
          fontFamily={specialFont}
          letterSpacing="wider"
          onClick={handleAnonymousLogin}
        >
          SKIP
        </Button>
      </Box>
    ) : null;

  const renderRegister =
    tab === 0 ? (
      <Text align="center" mb={4}>
        Don&apos;t have an account? Register{' '}
        <Button variant="link" size="lg" type="submit" w="auto" onClick={() => setTab(1)} mt={2}>
          here
        </Button>
        .
      </Text>
    ) : null;

  let renderAuthOverlay = (
    <Box>
      <Box px={6}>
        <Tabs
          options={tabOptions}
          onChange={(index: number) => setTab(index)}
          isFitted
          tabProps={{ fontSize: 'overlayTitle' }}
          panelProps={{ pt: 8, pb: handleGetTabPadding() }}
          index={tab}
        />
      </Box>
      {renderError}
      {renderRegister}
      {renderLostPassword}
      {renderSkipLink}
    </Box>
  );

  if (forgotPassword || recoverPassword) {
    const renderForm = forgotPassword ? (
      <ForgotPasswordForm url={url} repeatProps={{ textAlign: 'center' }} infoProps={{ textAlign: 'center' }} />
    ) : (
      <RecoverPassword
        onReturn={() => {
          setRecoverPassword(false);
          setForgotPassword(true);
        }}
        onRecover={() =>
          showToast({
            message: 'Password updated successfully.',
          })
        }
        requestProps={{
          py: 8,
        }}
        queryParams={queryParams}
        formProps={{
          columns: 1,
          spacing: 2,
          gridProps: {
            pt: 6,
            pb: 4,
          },
        }}
      />
    );
    const renderTitle = forgotPassword ? 'FORGOT PASSWORD' : 'PASSWORD RECOVERY';

    renderAuthOverlay = (
      <Box px={6}>
        <Box
          sx={{
            bg: 'black',
            textAlign: 'center',
            color: 'white',
            py: 3,
            px: 3,
          }}
        >
          <Heading as="h3" size="sm" fontSize="overlayTitle" fontWeight="medium">
            {renderTitle}
          </Heading>
        </Box>
        <Box mb={4}>{renderForm}</Box>
        <Box textAlign="center" mb={6}>
          <Button
            variant="link"
            py={2}
            onClick={() => {
              setRecoverPassword(false);
              setForgotPassword(false);
            }}
          >
            Back to login
          </Button>
        </Box>
      </Box>
    );
  }

  if (loading) {
    renderAuthOverlay = (
      <Flex p={10} justifyContent="center" alignItems="center">
        <Spinner size="lg" />
      </Flex>
    );
  }

  return (
    <Box>
      <Modal
        isOpen={modalOpen}
        trapFocus={window.location === window.parent.location}
        isCloseButton={false}
        closeOnEsc={false}
        closeOnOverlayClick={false}
        isCentered
        size={tab === 1 ? 'xl' : 'sm'}
        onClose={() => setModalOpen(false)}
      >
        <Box mt={loading ? 0 : -6}>{renderAuthOverlay}</Box>
      </Modal>
    </Box>
  );
};

export default AuthOverlay;
